import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import { createDirectStore } from 'direct-vuex';
import pick from 'lodash/pick';

import source, { SourceState } from '@/vuex/source';
import target, { TargetState } from '@/vuex/target';
import transform, { TransformState } from '@/vuex/transform';
import migration, { MigrationState } from '@/vuex/migration';
import read, { ReadState } from '@/vuex/read';
import ui, { UIState } from '@/vuex/ui';
import task, { TaskState } from '@/vuex/tasks';

const {
  store,
  rootActionContext,
  moduleActionContext,
  rootGetterContext,
  moduleGetterContext,
} = createDirectStore({
  modules: {
    source,
    read,
    target,
    transform,
    migration,
    ui,
    task,
  },
  plugins: [
    createPersistedState({
      reducer(state) {
        return pick(state, [
          // UI
          'ui.tabIndex',
          'ui.hideTransformSourcePreview',
          'ui.hideTransformTargetPreview',
          // Source
          'source.type',
          'source.adapter',
          // Read
          'read.read',
          'read.currentIndex',
          // Transform
          'transform.type',
          'transform.options',
          // Target
          'target.type',
          'target.adapter',
          'target.appId',
          'target.dataset',
          // Migration
          'migration.migrations',
          'migration.currentMigrationIndex',
        ]);
      },
    }),
  ],
});

store.original.watch(
  (_, getters) => getters['target/config'],
  target => store.dispatch.migration.updateCurrentMigration({ target })
);

store.original.watch(
  state => state.source.type,
  () => store.dispatch.read.reset({})
);

store.original.watch(
  (_, getters) => getters['source/config'],
  source => store.dispatch.migration.updateCurrentMigration({ source })
);

store.original.watch(
  (_, getters) => getters['transform/config'],
  transform => store.dispatch.migration.updateCurrentMigration({ transform })
);

export default store;

export {
  rootActionContext,
  moduleActionContext,
  rootGetterContext,
  moduleGetterContext,
};

export type AppState = {
  source: SourceState;
  read: ReadState;
  target: TargetState;
  transform: TransformState;
  migration: MigrationState;
  ui: UIState;
  task: TaskState;
};

export type AppStore = typeof store;

declare module 'vuex' {
  interface Store<S> {
    direct: AppStore;
  }
}
